import { z } from "zod";

/** Nombre maximal de définitions de métriques numériques par cabinet (portail). */
export const PORTAL_NUMERIC_DEFINITIONS_MAX = 50;

/** Bornes de l'intervalle (en jours) du rappel automatique de saisie des métriques. */
export const PORTAL_NUMERIC_REMINDER_MIN_DAYS = 1;
export const PORTAL_NUMERIC_REMINDER_MAX_DAYS = 90;

/** Métriques personnalisées (poids, score, minutes, etc.) — courbes côté portail. */
export const NumericMetricDefinitionSchema = z.object({
  id: z.string().min(1).max(64).regex(/^[a-z0-9_-]+$/i),
  label: z.string().min(1).max(120),
  unit: z.string().max(32).optional(),
  /** Cible affichée en pointillés sur le graphique (optionnel). */
  target: z.number().finite().optional(),
  /** Texte d’aide affiché au client (ex. infobulle à côté du libellé). */
  description: z.string().max(500).optional(),
  /** Rappel automatique si le client n’a pas saisi cette métrique depuis N jours. */
  reminderEnabled: z.boolean().optional(),
  reminderIntervalDays: z
    .number()
    .int()
    .min(PORTAL_NUMERIC_REMINDER_MIN_DAYS)
    .max(PORTAL_NUMERIC_REMINDER_MAX_DAYS)
    .optional(),
});

/** Sans `.strict()` : les clés inconnues (ex. ancien `wellnessMode`) sont ignorées lors du parse. */
export const ClientPortalFeaturesSchema = z.object({
  /** Activer le suivi par métriques numériques + définitions. */
  numericTrackingEnabled: z.boolean().optional(),
  numericDefinitions: z
    .array(NumericMetricDefinitionSchema)
    .max(PORTAL_NUMERIC_DEFINITIONS_MAX)
    .optional(),
  /** @deprecated Préférer reminderEnabled sur chaque numericDefinitions */
  numericReminderEnabled: z.boolean().optional(),
  /** @deprecated Préférer reminderIntervalDays sur chaque numericDefinitions */
  numericReminderIntervalDays: z
    .number()
    .int()
    .min(PORTAL_NUMERIC_REMINDER_MIN_DAYS)
    .max(PORTAL_NUMERIC_REMINDER_MAX_DAYS)
    .optional(),
});

export type ClientPortalFeatures = z.infer<typeof ClientPortalFeaturesSchema>;
export type NumericMetricDefinition = z.infer<typeof NumericMetricDefinitionSchema>;

export const NumericMetricEntryCreateSchema = z
  .object({
    metricId: z.string().min(1),
    value: z.number().finite(),
    /** Jour civil de la mesure (AAAA-MM-JJ), fuseau local côté client. */
    recordedOn: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, "Format date invalide (AAAA-MM-JJ)")
      .optional(),
  })
  .transform((data) => ({
    ...data,
    recordedOn:
      data.recordedOn ?? new Date().toISOString().slice(0, 10),
  }));

export type NumericMetricEntryCreateDto = z.infer<
  typeof NumericMetricEntryCreateSchema
>;

export const NumericMetricEntryUpdateSchema = z.object({
  value: z.number().finite(),
});

export type NumericMetricEntryUpdateDto = z.infer<
  typeof NumericMetricEntryUpdateSchema
>;
